"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isBareRunnableNodeType = exports.isFacilityRequiringCANodeType = exports.FACILITY_LESS_ALLOWED_CA_TYPES = exports.isCANodeType = exports.isPrimitiveNodeType = exports.BARE_ARTIFACT_TYPES = exports.PRIMITIVE_ARTIFACT_TYPES = exports.ARTIFACT_NODE_CATALOG = void 0;
/**
 * The single source of truth for the node-layer partition. Declared `as const`
 * so each entry's `layer` stays a literal — the layer sets/unions below are
 * *derived* from it at both the value and type level. Exhaustiveness (every
 * `ArtifactType` classified) and key-validity (no stray/typo keys) are enforced
 * by the `_AssertCatalog*` checks just after the literal.
 */
exports.ARTIFACT_NODE_CATALOG = {
    // ─── BARE (pure transform/pass-through → S3 CSV; no DB write, no external
    // side-effect, no CA manager, no BBPL run context; runnable in a standalone
    // graph run) ───
    userInputNeeded: {
        layer: 'bare',
        summary: 'Control marker; never executed by the runner — pipeline pauses for user input.',
    },
    rawSupplementalTape: {
        layer: 'bare',
        summary: 'Raw customer supplemental-tape upload; pass-through.',
    },
    rawZipTape: {
        layer: 'bare',
        summary: 'Raw zip upload; expanded by ingest-organizer, pass-through here.',
    },
    generatedSupplementalTape: {
        layer: 'bare',
        summary: "Captures (re-points to) the single input instance's S3 location as its output.",
    },
    intermediateTape: {
        layer: 'bare',
        summary: 'Runs the tape-builder transform over input buffers → CSV (createTransformedTape engine).',
    },
    // ─── DOMAIN (manager-free but NOT bare: writes Finley state, does an external
    // side-effect, materializes datalake partitions, or carries BBPL vocabulary;
    // dispatches/commits like `bare` but BBPL-only, so a bare run rejects it) ───
    uploadToDatalake: {
        layer: 'domain',
        summary: 'Sanitizes CSV → glue table + partition (reportingDate/fileVersion keys); best-effort Omni topic provisioning.',
    },
    waitForDataLakeTable: {
        layer: 'domain',
        summary: 'Enqueues a datalake hydration-wait job gating a downstream userInputNeeded node.',
    },
    forwardedTape: {
        layer: 'domain',
        summary: 'Pushes output tapes to a lender via SFTP (+ cap-markets Slack, tala overrides).',
    },
    orgProvidedInput: {
        layer: 'domain',
        summary: 'Parses single-value CSV → writes org_provided_values.',
    },
    orgProvidedInputs: {
        layer: 'domain',
        summary: 'Parses key/value CSV → bulk-writes org_provided_values.',
    },
    finStatement: {
        layer: 'domain',
        summary: 'Parses fin-statement CSV → writes financial_statements.',
    },
    rawLoanTape: {
        layer: 'domain',
        summary: 'Raw customer loan-tape upload; pass-through (BBPL).',
    },
    cashBalancesFeedContext: {
        layer: 'domain',
        summary: 'Holds cash-balance-feed context config; pass-through (BBPL).',
    },
    fundingApprovalReport: {
        layer: 'domain',
        summary: 'Funding-approval marker; pass-through (BBPL).',
    },
    // Tape-builder transform variants that share the createTransformedTape engine
    // but carry BBPL vocabulary.
    generatedLoanTape: {
        layer: 'domain',
        summary: 'Tape-builder transform producing a loan tape (BBPL).',
    },
    generatedCashBalanceFeed: {
        layer: 'domain',
        summary: 'Tape-builder transform producing a cash-balance feed (BBPL).',
    },
    generatedRepaymentTape: {
        layer: 'domain',
        summary: 'Tape-builder transform producing a repayment tape (BBPL).',
    },
    // Raw customer uploads for BBPL-specific tape kinds; pass-through.
    rawCashBalanceFeed: {
        layer: 'domain',
        summary: 'Raw customer cash-balance-feed upload; pass-through (BBPL).',
    },
    rawRepaymentTape: {
        layer: 'domain',
        summary: 'Raw customer repayment-tape upload; pass-through (BBPL).',
    },
    rawReceivableDocumentation: {
        layer: 'domain',
        summary: 'Raw receivable-documentation upload; pass-through (BBPL).',
    },
    configTape: {
        layer: 'domain',
        summary: 'Legacy config holder; never executed. Kept so older graphs still commit.',
    },
    // ─── CA (credit-agreement / credit-facility; need creditAgreementManager) ───
    internalRepaymentTape: {
        layer: 'ca',
        summary: 'CA: builds internal repayment tape from facility mechanics.',
    },
    cashResponseTape: {
        layer: 'ca',
        summary: 'CA: builds cash response tape.',
    },
    masterLoanTape: {
        layer: 'ca',
        summary: 'CA: assembles master loan tape across facilities.',
    },
    manualBankAccountReport: {
        layer: 'ca',
        summary: 'CA: ingests manual bank-account reports.',
    },
    assignedLoanTape: {
        layer: 'ca',
        summary: 'CA: assigns loans to facilities → assigned loan tape.',
    },
    assignedRepaymentTape: {
        layer: 'ca',
        summary: 'CA: assigns repayments to facilities.',
    },
    receivableResponseTapePreview: {
        layer: 'ca',
        summary: 'CA: preview of receivable response tape.',
    },
    receivableResponseTape: {
        layer: 'ca',
        summary: 'CA: builds receivable response tape.',
    },
    borrowingBaseReport: {
        layer: 'ca',
        summary: 'CA: computes + writes the borrowing-base report.',
    },
    directInsertBorrowingBaseReport: {
        layer: 'ca',
        summary: 'CA: directly inserts a borrowing-base report.',
    },
    directInsertPortfolioCovenant: {
        layer: 'ca',
        summary: 'CA: directly inserts a portfolio covenant.',
    },
    directInsertPortfolioCovenants: {
        layer: 'ca',
        summary: 'CA: directly inserts portfolio covenants.',
    },
    directInsertFinancialCovenants: {
        layer: 'ca',
        summary: 'CA: directly inserts financial covenants.',
    },
    receivableInvoice: {
        layer: 'ca',
        summary: 'CA: generates receivable invoices.',
    },
    fundingVerificationReport: {
        layer: 'ca',
        summary: 'CA: builds funding verification report.',
    },
    fundingRequest: {
        layer: 'ca',
        summary: 'CA: creates a funding request.',
    },
    receivableDocumentationVerificationReport: {
        layer: 'ca',
        summary: 'CA: verifies receivable documentation.',
    },
    valleyBankIngest: {
        layer: 'ca',
        summary: 'CA (facility-less-allowed): Valley Bank tape ingest.',
    },
    valleyBankSlsIngest: {
        layer: 'ca',
        summary: 'CA (facility-less-allowed): Valley Bank SLS tape ingest.',
    },
    valleyBankSlsCommitment: {
        layer: 'ca',
        summary: 'CA (facility-less-allowed): Valley Bank SLS commitment ingest.',
    },
    generatedCovenantLoanTape: {
        layer: 'ca',
        summary: 'CA: builds covenant loan tape from facility mechanics.',
    },
    generatedFinancialData: {
        layer: 'ca',
        summary: 'CA: derives financial data for a facility.',
    },
    dataLakeTape: {
        layer: 'ca',
        summary: 'CA: generates a CSV from a datalake query.',
    },
    limitedPartnersTape: {
        layer: 'ca',
        summary: 'CA: builds limited-partners tape.',
    },
};
const ALL_ARTIFACT_TYPES = Object.keys(exports.ARTIFACT_NODE_CATALOG);
const artifactTypesInLayer = (...layers) => {
    const wanted = new Set(layers);
    return ALL_ARTIFACT_TYPES.filter((type) => wanted.has(exports.ARTIFACT_NODE_CATALOG[type].layer));
};
/** The primitive (manager-free) artifact types — `bare ∪ domain`. */
exports.PRIMITIVE_ARTIFACT_TYPES = artifactTypesInLayer('bare', 'domain');
/** The bare (standalone-graph-run-runnable) artifact types. */
exports.BARE_ARTIFACT_TYPES = artifactTypesInLayer('bare');
const BARE_ARTIFACT_TYPE_SET = new Set(exports.BARE_ARTIFACT_TYPES);
const PRIMITIVE_ARTIFACT_TYPE_SET = new Set(exports.PRIMITIVE_ARTIFACT_TYPES);
/**
 * True if the artifact type belongs to the primitive (manager-free) umbrella
 * (`reusable ∪ lending`). Narrows to {@link PrimitiveArtifactType} so callers
 * can dispatch to the primitive registry / accept the node into a
 * primitives-only graph.
 */
const isPrimitiveNodeType = (type) => PRIMITIVE_ARTIFACT_TYPE_SET.has(type);
exports.isPrimitiveNodeType = isPrimitiveNodeType;
/**
 * True if the artifact type belongs to the CA (credit-agreement) layer.
 * Narrows to {@link CAArtifactType}. The exact negation of
 * {@link isPrimitiveNodeType}.
 */
const isCANodeType = (type) => !(0, exports.isPrimitiveNodeType)(type);
exports.isCANodeType = isCANodeType;
/**
 * CA node types that are nonetheless ALLOWED inside a primitives-only
 * (facility-less) graph. These bind their credit facilities at run time from
 * the ingested data (e.g. Valley Bank SLS maps CSV participants → facilities
 * via `accessors.creditFacility`), so their graph nodes legitimately carry no
 * node-level `creditFacilityId` and the document has an empty `cfIdToSlugMap`.
 *
 * They are classified CA (their tasks touch facility data), but the
 * facility-less-graph guardrail must not reject them — a real committed graph
 * (the "Valley Bank - SLS Borrowers" ingest pipeline) has exactly this shape.
 * See `docs/BBPL_Graph_Primitives_Decouple.md`.
 */
exports.FACILITY_LESS_ALLOWED_CA_TYPES = new Set(['valleyBankIngest', 'valleyBankSlsIngest', 'valleyBankSlsCommitment']);
/**
 * True if `type` is a CA node type that the facility-less-graph guardrail must
 * still reject (i.e. CA and not in {@link FACILITY_LESS_ALLOWED_CA_TYPES}).
 */
const isFacilityRequiringCANodeType = (type) => (0, exports.isCANodeType)(type) && !exports.FACILITY_LESS_ALLOWED_CA_TYPES.has(type);
exports.isFacilityRequiringCANodeType = isFacilityRequiringCANodeType;
/**
 * True if `type` may run in a bare (pipeline-less) graph run: its task is a
 * pure transform / pass-through that writes no Finley state, does no external
 * side-effect, and needs no BBPL run context — i.e. its catalog layer is
 * `bare`. Narrows to {@link BareArtifactType}.
 *
 * This is the node-type half of the bare-run admission check; the operation
 * half is {@link operationRequiresExtractor} in `uiNodes/tasks` (a bare run also
 * rejects graphs with extractor-backed operations).
 *
 * See `docs/BBPL_Graph_Primitives_Decouple.md` §6c.
 */
const isBareRunnableNodeType = (type) => BARE_ARTIFACT_TYPE_SET.has(type);
exports.isBareRunnableNodeType = isBareRunnableNodeType;
//# sourceMappingURL=nodeLayer.js.map